/**
 * Lease Ledger Tab Component
 * Main ledger view for landlords to see charges, payments, and manage the ledger
 */

import { useState, useEffect } from 'react';
import {
  getLedgerEntries,
  createManualCharge,
  createCredit,
  listPendingCredits,
  cancelPendingCredit,
  type LedgerEntry,
  type CreateManualChargeInput,
  type CreateCreditInput,
  type PendingCredit,
  ApiClientError,
} from '../../lib/api';
import { formatLocalDate } from '../../utils/date';
import { BalanceSummaryCard } from './BalanceSummaryCard';
import { LedgerEntryList } from './LedgerEntryList';
import { AddChargeModal } from './AddChargeModal';
import { IssueCreditModal } from './IssueCreditModal';
import { HelpBlock, HelpTooltip } from '../ui/HelpTooltip';

interface LeaseLedgerTabProps {
  leaseId: string;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  /** BR-LL-11: true once the lease is archived — the ledger is still viewable, but no
   *  manual charge/credit/cancel mutation is offered (the API rejects them regardless). */
  readOnly?: boolean;
}

export function LeaseLedgerTab({ leaseId, onError, onSuccess, readOnly = false }: LeaseLedgerTabProps) {
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [pendingCredits, setPendingCredits] = useState<PendingCredit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddChargeModalOpen, setIsAddChargeModalOpen] = useState(false);
  const [isIssueCreditModalOpen, setIsIssueCreditModalOpen] = useState(false);

  useEffect(() => {
    loadLedger();
    loadPendingCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId]);

  async function loadLedger() {
    try {
      setIsLoading(true);
      const response = await getLedgerEntries(leaseId);
      setEntries(response.entries);
      setBalance(response.currentBalance);
    } catch (err) {
      onError('Failed to load ledger');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadPendingCredits() {
    try {
      const response = await listPendingCredits(leaseId);
      setPendingCredits(response.pendingCredits);
    } catch (err) {
      console.error('Failed to load pending credits', err);
    }
  }

  async function handleAddCharge(data: CreateManualChargeInput) {
    try {
      await createManualCharge(leaseId, data);
      await loadLedger();
      setIsAddChargeModalOpen(false);
      onSuccess('Charge added successfully');
    } catch (err) {
      if (err instanceof ApiClientError) {
        throw new Error(err.message);
      }
      throw new Error('Failed to add charge');
    }
  }

  async function handleIssueCredit(data: CreateCreditInput) {
    try {
      const result = await createCredit(leaseId, data);
      await loadLedger();
      await loadPendingCredits();
      setIsIssueCreditModalOpen(false);

      if (result.type === 'immediate') {
        onSuccess('Credit issued successfully');
      } else {
        onSuccess('Credit scheduled successfully for ' + formatLocalDate(result.pendingCredit.effectiveDate));
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        throw new Error(err.message);
      }
      throw new Error('Failed to issue credit');
    }
  }

  async function handleCancelPendingCredit(pendingCreditId: string) {
    if (!confirm('Are you sure you want to cancel this pending credit?')) {
      return;
    }

    try {
      await cancelPendingCredit(pendingCreditId);
      await loadPendingCredits();
      onSuccess('Pending credit cancelled successfully');
    } catch (err) {
      onError('Failed to cancel pending credit');
      console.error(err);
    }
  }

  return (
    <div data-testid="lease-ledger-tab" className="flex flex-col gap-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold m-0">
            Ledger
          </h2>
          <p className="text-sm text-muted-foreground mt-1 m-0">
            View all charges, payments, and credits for this lease
          </p>
        </div>
        {!readOnly && (
          <div className="flex gap-3 flex-wrap">
            <button
              data-testid="add-charge-button"
              onClick={() => setIsAddChargeModalOpen(true)}
              className="px-4 py-2.5 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent min-h-[44px]"
            >
              Add Charge
            </button>
            <button
              data-testid="issue-credit-button"
              onClick={() => setIsIssueCreditModalOpen(true)}
              className="px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 min-h-[44px]"
            >
              Issue Credit
            </button>
          </div>
        )}
      </div>

      {readOnly && (
        <div
          className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
          data-testid="ledger-archived-readonly-notice"
          role="status"
        >
          This lease is archived and read-only. No new charges, credits or payments can be recorded.
        </div>
      )}

      {/* Help block */}
      <HelpBlock
        summary="The ledger is the official record of all charges and payments for this lease. The running balance shows what the tenant currently owes (or any credit in their favor)."
        details={"Entry types:\n\u2022 Charges (red) \u2014 Rent, late fees, and other amounts the tenant owes. Recurring charges are generated automatically each month.\n\u2022 Payments (green) \u2014 Money received from the tenant, whether via ACH or external payment.\n\u2022 Credits (green) \u2014 Adjustments you issue to reduce the balance (e.g., overpayment correction, move-in discount).\n\u2022 Reversals (red) \u2014 Returned or failed payments that increase the balance back.\n\nAdd Charge \u2014 Use for one-time charges outside the normal rent cycle (e.g., repair costs, key replacement).\nIssue Credit \u2014 Reduce the tenant's balance immediately or schedule a credit for a future date.\n\nThe ledger is append-only \u2014 entries are never edited or deleted. Corrections are made by adding new entries, which creates a clear audit trail."}
      />

      {/* Balance Summary */}
      <BalanceSummaryCard balance={balance} isLoading={isLoading} />

      {/* Pending Credits */}
      {pendingCredits.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4 m-0 inline-flex items-center gap-1.5">
            Pending Credits
            <HelpTooltip text="Credits scheduled for a future date. They will be applied to the tenant's balance automatically on their effective date. You can cancel a pending credit before it takes effect." />
          </h3>
          <div className="flex flex-col gap-2">
            {pendingCredits.map((credit) => (
              <div
                key={credit.id}
                data-testid="pending-credit-item"
                className="bg-card rounded-lg border border-border p-4 flex justify-between items-center"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold">
                      ${(Math.abs(credit.amount) / 100).toFixed(2)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {credit.description}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Scheduled for {formatLocalDate(credit.effectiveDate)}
                  </div>
                </div>
                {!readOnly && (
                  <button
                    data-testid="cancel-pending-credit-button"
                    onClick={() => handleCancelPendingCredit(credit.id)}
                    className="px-3 py-1.5 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent min-h-[44px]"
                  >
                    Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ledger Entries */}
      <div>
        <h3 className="text-lg font-semibold mb-4 m-0">
          Transaction History
        </h3>
        <LedgerEntryList entries={entries} isLoading={isLoading} />
      </div>

      {/* Modals */}
      {isAddChargeModalOpen && (
        <AddChargeModal
          onSave={handleAddCharge}
          onCancel={() => setIsAddChargeModalOpen(false)}
        />
      )}

      {isIssueCreditModalOpen && (
        <IssueCreditModal
          onSave={handleIssueCredit}
          onCancel={() => setIsIssueCreditModalOpen(false)}
        />
      )}
    </div>
  );
}
